import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Input, Row } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import PasswordHint from '../../PasswordHint/PasswordHint';
import './UserProfileChangePassword.less';
import { AxiosResponse } from 'axios';
import usePasswordValidate from '../../../lib/utils/usePasswordValidate';

interface IProps {
  handleChangePassword: (args: {
    oldPassword: string;
    newPassword: string;
  }) => Promise<void | AxiosResponse> | undefined;
}

const UserProfileChangePassword = ({ handleChangePassword }: IProps) => {
  const [passwordForm] = useForm();

  const [isPassButtonDisabled, setIsPassButtonDisabled] = useState(true);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [isOldPasswordValid, setIsOldPasswordValid] = useState(true);

  const onReset = () => {
    passwordForm.resetFields();
    setIsOldPasswordValid(true);
    setIsPasswordValid(false);
    setIsPassButtonDisabled(true);
  };

  useEffect(() => {
    return () => {
      onReset();
    };
  }, []);

  const {
    isRepeatedPasswordValidator,
    isPasswordValidator,
    handleKeyPress,
    handlePaste,
    isPasswordSameValidator,
    isPasswordReusedValidator,
  } = usePasswordValidate();

  // Для нового пароля проверки (чтобы показывать одно сообщение об ошибке одновременно)
  const combinedPasswordValidator = async () => {
    try {
      await isPasswordSameValidator({ form: passwordForm });
    } catch {
      throw new Error(' ');
    }

    try {
      await isPasswordValidator({ form: passwordForm });
    } catch {
      throw new Error('Пароль слишком простой');
    }

    try {
      await isPasswordReusedValidator({ form: passwordForm });
    } catch {
      throw new Error('Новый пароль совпадает с текущим');
    }

    return Promise.resolve();
  };

  // Для проверки всех полей (для кнопки сохранить)
  const allCombinedPasswordValidator = async () => {
    try {
      await isPasswordValidator({ form: passwordForm });
    } catch {
      throw new Error('Пароль слишком простой');
    }

    try {
      await isPasswordReusedValidator({ form: passwordForm });
    } catch {
      throw new Error('Новый пароль совпадает с текущим');
    }

    try {
      await isRepeatedPasswordValidator({ form: passwordForm });
    } catch {
      throw new Error('Пароль не совпадает');
    }

    return Promise.resolve();
  };

  const onFinish = (values) => {
    handleChangePassword({ oldPassword: values.oldPassword, newPassword: values.newPassword })?.then((res) => {
      const responseAxios = res as AxiosResponse & { response: any };

      if (responseAxios.status === 204) {
        onReset();
      }

      if (responseAxios && responseAxios.response)
        if (responseAxios.response?.data?.code === 1000) {
          setIsOldPasswordValid(false);
        }
    });
  };

  const passwordChangeInfo = () => {
    return (
      <Col span={24}>
        <PasswordHint />
        <Form
          form={passwordForm}
          name="basic"
          initialValues={{
            remember: true,
          }}
          layout="vertical"
          onFinish={onFinish}
          style={{ marginTop: '24px' }}
          onValuesChange={(field, fields) => {
            combinedPasswordValidator()
              .then(() => setIsPasswordValid(true))
              .catch(() => setIsPasswordValid(false));

            if (Object.values(fields).some((item) => item === undefined || item === '')) {
              setIsPassButtonDisabled(true);
            } else {
              allCombinedPasswordValidator()
                .then(() => setIsPassButtonDisabled(false))
                .catch(() => setIsPassButtonDisabled(true));
            }
          }}
        >
          <Form.Item
            label="Текущий пароль"
            name="oldPassword"
            dependencies={['newPassword', 'repeatedPassword', 'oldPassword']}
            className="mt10"
            rules={[
              {
                required: true,
                message: '',
              },
            ]}
            validateStatus={!isOldPasswordValid ? 'error' : undefined}
            help={!isOldPasswordValid ? 'Введённый пароль не совпадает с текущим' : undefined}
          >
            <Input.Password
              className="change-password-input"
              onChange={() => setIsOldPasswordValid(true)}
           /*   onKeyPress={handleKeyPress}*/
              onPaste={handlePaste}
            />
          </Form.Item>
          <Form.Item
            dependencies={['newPassword', 'repeatedPassword', 'oldPassword']}
            label="Новый пароль"
            name="newPassword"
            validateStatus={isPasswordValid ? 'success' : undefined}
            help={isPasswordValid ? 'Надёжный пароль' : undefined}
            rules={[
              {
                required: true,
                message: '',
              },
              {
                validator: combinedPasswordValidator,
              },
            ]}
            className={`mt10 ${isPasswordValid ? 'validate-success-message' : ''}`}
          >
            <Input.Password className="change-password-input" onKeyPress={handleKeyPress} onPaste={handlePaste} />
          </Form.Item>
          <Form.Item
            label="Подтвердите пароль"
            name="repeatedPassword"
            dependencies={['newPassword', 'repeatedPassword', 'oldPassword']}
            rules={[
              {
                required: true,
                message: '',
              },
              {
                validator: () => isRepeatedPasswordValidator({ form: passwordForm }),
                message: 'Пароль не совпадает',
              },
            ]}
            className="mt10"
          >
            <Input.Password className="change-password-input" onKeyPress={handleKeyPress} onPaste={handlePaste} />
          </Form.Item>
        </Form>
      </Col>
    );
  };

  return (
    <div className="user-profile-change-password-container ml20 mr20" style={{ width: '100%' }}>
      <Row className="user-profile-change-password">
        <Col span={24} className="user-profile-change-password__title">
          <span>
            Пароль должен быть не менее 8 символов и содержать хотя бы по одному символу из четырёх следующих групп:
          </span>
        </Col>
        <div className="user-profile-change-password__content">{passwordChangeInfo()}</div>
      </Row>
      <div className="mt10 display-flex" style={{ alignItems: 'start' }}>
        <Button style={{ width: 247 }} onClick={() => passwordForm.submit()} disabled={isPassButtonDisabled}>
          Сохранить изменения
        </Button>
      </div>
    </div>
  );
};

export default UserProfileChangePassword;
